<script setup>
import { computed, ref } from 'vue';
import IconChevronRight from '../../icons/IconChevronRight.vue';
import ScheduleRow from './ScheduleRow.vue';

const props = defineProps({
  count: { type: Number, default: 0 },
  throughLabel: { type: String, default: '' },
  titles: { type: Array, default: () => [] },
  more: { type: Boolean, default: false },
  rows: { type: Array, default: () => [] },
  // Whether the week has days left in it after today, which the server
  // decides. Without it a quiet week and a missing feature look identical.
  hasDaysLeft: { type: Boolean, default: false },
});

defineEmits(['open', 'act']);

// Page-lifetime only, and deliberately not persisted: it is a glance, not a
// preference.
const expanded = ref(false);

const summary = computed(() => {
  const titles = props.titles.join(' · ');

  return props.more ? `${titles} · …` : titles;
});
</script>

<template>
  <!-- Nothing to say once the week is over — "3 more before Sunday" on a Sunday
       is nonsense, so the server says the week is done and this renders
       nothing. Any other day the line is present whether or not anything is
       booked: a component that disappears when it is empty is indistinguishable
       from one that was never built, which is how this was read. -->
  <div v-if="hasDaysLeft" class="week-line" data-testid="week-line">
    <!-- Not a button: there is nothing under it to open. -->
    <p v-if="count === 0" class="week-line__empty" data-testid="week-empty">
      Nothing else before {{ throughLabel }}.
    </p>

    <button
      v-else
      type="button"
      class="week-line__toggle"
      :aria-expanded="expanded ? 'true' : 'false'"
      data-testid="week-toggle"
      @click="expanded = !expanded"
    >
      <!-- The same icon the rows below use. This was a rotated `›` character,
           which drifted from the sibling rows at every font size. -->
      <span class="week-line__caret" :class="{ 'week-line__caret--open': expanded }" aria-hidden="true">
        <IconChevronRight color="currentColor" />
      </span>
      <span class="week-line__count">{{ count }} more before {{ throughLabel }}</span>
      <span class="week-line__titles">{{ summary }}</span>
    </button>

    <!-- Expands in place. It never navigates: the whole point is a look at the
         week without leaving the Overview. -->
    <div v-if="expanded" class="week-line__rows">
      <ScheduleRow
        v-for="row in rows"
        :key="row.id"
        :row="row"
        show-day
        :testid="`week-row-${row.id}`"
        @open="(r, el) => $emit('open', r, el)"
        @act="(r) => $emit('act', r)"
      />
    </div>
  </div>
</template>

<style scoped>
.week-line {
  margin-top: 10px;
  font-family: var(--xl-font-family);
}

.week-line__toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 4px;
  border: 0;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.week-line__toggle:focus-visible {
  outline: 2px solid var(--xl-color-primary);
  outline-offset: -2px;
}

.week-line__empty {
  margin: 0;
  padding: 12px 4px 12px 30px;
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-outline);
}

.week-line__caret {
  display: inline-flex;
  color: var(--xl-color-outline);
  transition: transform 150ms ease;
}

.week-line__caret--open {
  transform: rotate(90deg);
}

.week-line__count {
  font-size: var(--xl-type-body-medium-size);
  font-weight: var(--xl-font-weight-bold);
  color: var(--xl-color-on-surface);
}

.week-line__titles {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--xl-type-body-medium-size);
  color: var(--xl-color-outline);
}

@media (prefers-reduced-motion: reduce) {
  .week-line__caret {
    transition: none;
  }
}
</style>
